import type { SVGProps } from 'react';

/** Names of the icons bundled with the UI library. */
export type IconName =
  | 'home'
  | 'folder'
  | 'settings'
  | 'shield'
  | 'plus'
  | 'search'
  | 'chevron-right'
  | 'chevron-left'
  | 'check'
  | 'trash'
  | 'copy'
  | 'edit'
  | 'globe'
  | 'bell'
  | 'close'
  | 'arrow-left'
  | 'sparkles'
  | 'sun'
  | 'moon'
  | 'palette'
  | 'user'
  | 'logout'
  | 'image'
  | 'upload'
  | 'archive'
  | 'more'
  | 'chevron-up'
  | 'chevron-down'
  | 'history'
  | 'crown'
  | 'smile'
  | 'bookmark'
  | 'help'
  | 'telegram'
  | 'sparkle-4';

// Each entry is the inner markup of a 24x24 icon drawn with strokes.
const PATHS: Record<IconName, JSX.Element> = {
  home: <path d="M3 10.5 12 3l9 7.5M5 9.5V20a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V9.5" />,
  folder: <path d="M3 7a2 2 0 0 1 2-2h4l2 2.5h6a2 2 0 0 1 2 2V17a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />,
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 7 19.4a1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.6 1.6 0 0 0 3 15a1.6 1.6 0 0 0-1.5-1H1a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 3 9a1.6 1.6 0 0 0-.3-1.8l-.1-.1A2 2 0 1 1 5.4 4.3l.1.1A1.6 1.6 0 0 0 7 4.7 1.6 1.6 0 0 0 8 3.2V3a2 2 0 1 1 4 0v.1A1.6 1.6 0 0 0 15 4.7a1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1A1.6 1.6 0 0 0 21 9v.1" />
    </>
  ),
  shield: <path d="M12 3l7 3v5c0 4.5-3 8.5-7 10-4-1.5-7-5.5-7-10V6l7-3Z" />,
  plus: <path d="M12 5v14M5 12h14" />,
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </>
  ),
  'chevron-right': <path d="m9 6 6 6-6 6" />,
  'chevron-left': <path d="m15 6-6 6 6 6" />,
  check: <path d="m5 12 5 5L20 7" />,
  trash: <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m-9 0 1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" />,
  copy: (
    <>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M6 15H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1" />
    </>
  ),
  edit: <path d="M4 20h4L18.5 9.5a2.1 2.1 0 0 0-3-3L5 17v3ZM13.5 6.5l3 3" />,
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.5 2.5 2.5 15 0 18M12 3c-2.5 2.5-2.5 15 0 18" />
    </>
  ),
  bell: <path d="M6 9a6 6 0 1 1 12 0c0 5 2 6 2 6H4s2-1 2-6M10 20a2 2 0 0 0 4 0" />,
  close: <path d="M6 6l12 12M18 6 6 18" />,
  'arrow-left': <path d="M20 12H4m6-6-6 6 6 6" />,
  sparkles: <path d="M12 4l1.6 4.4L18 10l-4.4 1.6L12 16l-1.6-4.4L6 10l4.4-1.6L12 4ZM19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8L19 15Z" />,
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </>
  ),
  moon: <path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5Z" />,
  palette: (
    <>
      <path d="M12 3a9 9 0 0 0 0 18c1.7 0 2-1.2 1.3-2-1-1.2-.2-2.5 1.2-2.5H17a4 4 0 0 0 4-4c0-4.4-4-9.5-9-9.5Z" />
      <circle cx="7.5" cy="11" r="1" />
      <circle cx="11" cy="7.5" r="1" />
      <circle cx="15" cy="8.5" r="1" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>
  ),
  logout: <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 17l5-5-5-5M15 12H3" />,
  image: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="8.5" cy="9.5" r="1.5" />
      <path d="m4 18 5-5 4 4 3-3 4 4" />
    </>
  ),
  upload: <path d="M12 15V4m0 0L8 8m4-4 4 4M4 17v1a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-1" />,
  archive: (
    <>
      <rect x="3" y="4" width="18" height="4" rx="1" />
      <path d="M5 8v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8M10 12h4" />
    </>
  ),
  more: (
    <>
      <circle cx="12" cy="5" r="1.2" />
      <circle cx="12" cy="12" r="1.2" />
      <circle cx="12" cy="19" r="1.2" />
    </>
  ),
  'chevron-up': <path d="m6 15 6-6 6 6" />,
  'chevron-down': <path d="m6 9 6 6 6-6" />,
  history: <path d="M3 12a9 9 0 1 0 3-6.7L3 8m0-5v5h5M12 7v5l3 2" />,
  // Premium — a slim three-peak crown resting on a thin band.
  crown: (
    <>
      <path d="M3.6 8.1 7.9 11 12 4.8 16.1 11l4.3-2.9-1.6 9.3H5.2L3.6 8.1Z" />
      <path d="M5.9 20.2h12.2" />
    </>
  ),
  // Emoji presets — a celestial smile with a small star spark.
  smile: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.4 14a4.4 4.4 0 0 0 7.2 0" />
      <path d="M9 9.4h.01M15 9.4h.01" />
    </>
  ),
  // Astral Channels — a bookmark holding a four-point star.
  bookmark: (
    <>
      <path d="M6.5 4.6a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v15.9L12 16.9l-5.5 3.6V4.6Z" />
      <path d="M12 7.4c.24 2.06.7 2.52 2.76 2.76-2.06.24-2.52.7-2.76 2.76-.24-2.06-.7-2.52-2.76-2.76 2.06-.24 2.52-.7 2.76-2.76Z" />
    </>
  ),
  // Guide — a question mark in a circle.
  help: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.3a2.5 2.5 0 1 1 3.3 2.4c-.6.22-.95.78-.95 1.42v.42" />
      <path d="M12 16.9h.01" />
    </>
  ),
  // Official channel — the Telegram paper plane.
  telegram: (
    <>
      <path d="M21.4 4.4 2.85 11.25a.5.5 0 0 0 .04.94l4.63 1.4 1.73 5.16a.5.5 0 0 0 .86.18l2.4-2.6 4.55 3.35a.5.5 0 0 0 .78-.28l3.9-14.36a.5.5 0 0 0-.34-.64Z" />
      <path d="m9.55 13.6 9.15-7.45-6.6 9.2" />
    </>
  ),
  // The Astral mark — a concave four-point sparkle.
  'sparkle-4': (
    <path d="M12 3c.6 5.4 3.6 8.4 9 9-5.4.6-8.4 3.6-9 9-.6-5.4-3.6-8.4-9-9 5.4-.6 8.4-3.6 9-9Z" />
  ),
};

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  /** Pixel size (width & height). Default 24. */
  size?: number;
}

/** A crisp, stroke-based icon. Inherits `currentColor`. */
export function Icon({ name, size = 24, strokeWidth = 1.8, ...rest }: IconProps): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}
